// src/app/services/service.service.ts

import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

// --- Type Definitions (Reused from previous step) ---

/**
 * Interface representing the data structure for creating/updating a service.
 */
interface ServicePayload {
  name: string;
  description: string;
  departmentId: string | null;
  price: number | null;
  isActive: boolean;
}

/**
 * Interface representing a single service returned by the API (including ID).
 */
interface Service {
  id: string; // Using 'id' based on your sample data
  name: string;
  description: string;
  departmentId: string;
  price: string | number;
  isActive: boolean;
  department: {
    id: string;
    name: string;
    // ... other department fields
  };
}

/**
 * Interface representing the structure of a paginated list response.
 */
interface PaginatedServicesResponse {
  services: Service[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

/**
 * Interface representing a Department.
 */
interface Department {
  id: string;
  name: string;
  description: string;
  // ... other department fields
}

/**
 * Interface for a paginated list of Departments.
 */
interface PaginatedDepartmentsResponse {
  departments: Department[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

@Injectable({
  providedIn: 'root',
})
export class ServiceService {
  private baseUrl = environment.apiUrl;
  private servicesUrl = `${this.baseUrl}/services`;
  // New API path for departments
  private departmentsUrl = `${this.baseUrl}/service-departments`;

  constructor(private http: HttpClient) {}

  // =========================================================
  //                  SERVICE CRUD OPERATIONS
  // =========================================================

  /**
   * Fetches a paginated list of services from the API.
   */
  getServices(
    page: number,
    limit: number,
    search: string = ''
  ): Observable<PaginatedServicesResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<PaginatedServicesResponse>(this.servicesUrl, {
      params,
    });
  }

  /**
   * Fetches a single service by ID.
   */
  getServiceById(id: string): Observable<Service> {
    return this.http.get<Service>(`${this.servicesUrl}/${id}`);
  }

  /**
   * Creates a new service.
   */
  createService(serviceData: ServicePayload): Observable<Service> {
    return this.http.post<Service>(this.servicesUrl, serviceData);
  }

  /**
   * Updates an existing service.
   */
  updateService(id: string, serviceData: ServicePayload): Observable<Service> {
    return this.http.patch<Service>(`${this.servicesUrl}/${id}`, serviceData);
  }

  /**
   * Deletes a service by ID.
   */
  deleteService(id: string): Observable<any> {
    return this.http.delete(`${this.servicesUrl}/${id}`);
  }

  // =========================================================
  //                  DEPARTMENT API CALLS (NEW)
  // =========================================================

  /**
   * Fetches a paginated list of service departments.
   * API: /service-departments
   */
  getDepartments(
    page: number,
    limit: number
  ): Observable<PaginatedDepartmentsResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    // NOTE: If your API returns the raw array of departments instead of a paginated object,
    // change the return type to Observable<Department[]>.
    return this.http.get<PaginatedDepartmentsResponse>(this.departmentsUrl, {
      params,
    });
  }

  /**
   * Fetches a single service department by ID.
   * API: /service-departments/{id}
   */
  getDepartmentById(id: string): Observable<Department> {
    return this.http.get<Department>(`${this.departmentsUrl}/${id}`);
  }

  getHsnCode(): Observable<any> {
    return this.http.get(`${this.baseUrl}/services/generate-hsn`);
  }
}
